"use client";

import { c as _c } from "react/compiler-runtime";
import { Film, ImagePlus, Loader2, Music, Plus, Sparkles, X } from "lucide-react";
import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { getVideoDurations, getVideoSizeLabel, getVideoSizes, type VideoDuration, type VideoFrameInputs, type VideoInputImage, type VideoSize } from "@/lib/video-gen";
import { useI18n } from "@llmgateway/i18n/react";
interface VideoControlsProps {
  prompt: string;
  setPrompt: (prompt: string) => void;
  selectedModels: string[];
  videoSize: VideoSize;
  setVideoSize: (value: VideoSize) => void;
  videoDuration: VideoDuration;
  setVideoDuration: (value: VideoDuration) => void;
  audioEnabled: boolean;
  setAudioEnabled: (value: boolean) => void;
  audioToggleDisabled: boolean;
  audioToggleDisabledReason?: string;
  canUseFrameInputs: boolean;
  canUseReferenceInputs: boolean;
  canUseReferenceVideoInputs: boolean;
  canUseReferenceAudioInputs: boolean;
  frameInputs: VideoFrameInputs;
  setFrameInputs: Dispatch<SetStateAction<VideoFrameInputs>>;
  referenceImages: VideoInputImage[];
  setReferenceImages: Dispatch<SetStateAction<VideoInputImage[]>>;
  referenceVideos: string[];
  setReferenceVideos: Dispatch<SetStateAction<string[]>>;
  referenceAudios: string[];
  setReferenceAudios: Dispatch<SetStateAction<string[]>>;
  supportedVideoSizes: VideoSize[];
  supportedVideoDurations: VideoDuration[];
  isGenerating: boolean;
  onGenerate: () => void;
  imageInputRequired?: boolean;
}
type UploadTarget = "frame-start" | "frame-end" | "reference";
export function VideoControls(t0) {
  const $ = _c(202);
  const {
    prompt,
    setPrompt,
    selectedModels,
    videoSize,
    setVideoSize,
    videoDuration,
    setVideoDuration,
    audioEnabled,
    setAudioEnabled,
    audioToggleDisabled,
    audioToggleDisabledReason,
    canUseFrameInputs,
    canUseReferenceInputs,
    canUseReferenceVideoInputs,
    canUseReferenceAudioInputs,
    frameInputs,
    setFrameInputs,
    referenceImages,
    setReferenceImages,
    referenceVideos,
    setReferenceVideos,
    referenceAudios,
    setReferenceAudios,
    supportedVideoSizes,
    supportedVideoDurations,
    isGenerating,
    onGenerate,
    imageInputRequired
  } = t0;
  const {
    t
  } = useI18n();
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadTarget, setUploadTarget] = useState("frame-start");
  const [referenceVideoDraft, setReferenceVideoDraft] = useState("");
  let t1;
  if ($[0] !== referenceVideoDraft || $[1] !== setFrameInputs || $[2] !== setReferenceVideos) {
    t1 = () => {
      const url = referenceVideoDraft.trim();
      if (!url || !/^https:\/\//i.test(url)) {
        return;
      }
      setFrameInputs({
        start: null,
        end: null
      });
      setReferenceVideos(prev => prev.length >= 3 || prev.includes(url) ? prev : [...prev, url]);
      setReferenceVideoDraft("");
    };
    $[0] = referenceVideoDraft;
    $[1] = setFrameInputs;
    $[2] = setReferenceVideos;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const addReferenceVideo = t1;
  let t2;
  if ($[4] !== setReferenceVideos) {
    t2 = index => {
      setReferenceVideos(prev_0 => prev_0.filter((_, i) => i !== index));
    };
    $[4] = setReferenceVideos;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const removeReferenceVideo = t2;
  const [referenceAudioDraft, setReferenceAudioDraft] = useState("");
  let t3;
  if ($[6] !== referenceAudioDraft || $[7] !== setFrameInputs || $[8] !== setReferenceAudios) {
    t3 = () => {
      const url_0 = referenceAudioDraft.trim();
      if (!url_0 || !/^https:\/\//i.test(url_0)) {
        return;
      }
      setFrameInputs({
        start: null,
        end: null
      });
      setReferenceAudios(prev_1 => prev_1.length >= 3 || prev_1.includes(url_0) ? prev_1 : [...prev_1, url_0]);
      setReferenceAudioDraft("");
    };
    $[6] = referenceAudioDraft;
    $[7] = setFrameInputs;
    $[8] = setReferenceAudios;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const addReferenceAudio = t3;
  let t4;
  if ($[10] !== setReferenceAudios) {
    t4 = index_0 => {
      setReferenceAudios(prev_2 => prev_2.filter((__0, i_0) => i_0 !== index_0));
    };
    $[10] = setReferenceAudios;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const removeReferenceAudio = t4;
  const hasImageInput = !!frameInputs.start;
  const missingRequiredImage = imageInputRequired && !hasImageInput;
  let t5;
  if ($[12] !== missingRequiredImage || $[13] !== prompt || $[14] !== selectedModels) {
    t5 = prompt.trim().length > 0 && selectedModels.length > 0 && !missingRequiredImage;
    $[12] = missingRequiredImage;
    $[13] = prompt;
    $[14] = selectedModels;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const canGenerate = t5;
  const canAcceptInput = canUseFrameInputs || canUseReferenceInputs;
  const defaultUploadTarget = !canUseReferenceInputs ? frameInputs.start ? "frame-end" : "frame-start" : !canUseFrameInputs || uploadTarget === "reference" || referenceImages.length > 0 ? "reference" : frameInputs.start ? "frame-end" : "frame-start";
  let t6;
  if ($[16] !== canAcceptInput || $[17] !== canUseFrameInputs || $[18] !== canUseReferenceInputs || $[19] !== defaultUploadTarget || $[20] !== setFrameInputs || $[21] !== setReferenceAudios || $[22] !== setReferenceImages || $[23] !== setReferenceVideos) {
    t6 = (file, explicitTarget) => {
      if (!canAcceptInput || !file.type.startsWith("image/")) {
        return;
      }
      const target = explicitTarget ?? defaultUploadTarget;
      if ((target === "frame-start" || target === "frame-end") && !canUseFrameInputs) {
        return;
      }
      if (target === "reference" && !canUseReferenceInputs) {
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const nextImage = {
          dataUrl: String(reader.result ?? ""),
          mediaType: file.type
        };
        if (target === "frame-start") {
          setReferenceImages([]);
          setReferenceVideos([]);
          setReferenceAudios([]);
          setFrameInputs(prev_3 => ({
            ...prev_3,
            start: nextImage
          }));
          return;
        }
        if (target === "frame-end") {
          setReferenceImages([]);
          setReferenceVideos([]);
          setReferenceAudios([]);
          setFrameInputs(prev_4 => ({
            ...prev_4,
            end: nextImage
          }));
          return;
        }
        setFrameInputs({
          start: null,
          end: null
        });
        setReferenceImages(prev_5 => {
          if (prev_5.length >= 3) {
            return prev_5;
          }
          return [...prev_5, nextImage];
        });
      };
      reader.readAsDataURL(file);
    };
    $[16] = canAcceptInput;
    $[17] = canUseFrameInputs;
    $[18] = canUseReferenceInputs;
    $[19] = defaultUploadTarget;
    $[20] = setFrameInputs;
    $[21] = setReferenceAudios;
    $[22] = setReferenceImages;
    $[23] = setReferenceVideos;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const addImageFile = t6;
  let t7;
  if ($[25] !== canGenerate || $[26] !== isGenerating || $[27] !== onGenerate) {
    t7 = e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (canGenerate && !isGenerating) {
          onGenerate();
        }
      }
    };
    $[25] = canGenerate;
    $[26] = isGenerating;
    $[27] = onGenerate;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const handleKeyDown = t7;
  let t8;
  if ($[29] !== addImageFile || $[30] !== referenceImages.length || $[31] !== uploadTarget) {
    t8 = e_0 => {
      const files = Array.from(e_0.target.files ?? []);
      for (const file_0 of files) {
        if (uploadTarget === "reference" && referenceImages.length >= 3) {
          break;
        }
        addImageFile(file_0, uploadTarget);
        if (uploadTarget !== "reference") {
          break;
        }
      }
      e_0.target.value = "";
    };
    $[29] = addImageFile;
    $[30] = referenceImages.length;
    $[31] = uploadTarget;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const handleFileSelect = t8;
  let t9;
  if ($[33] !== addImageFile || $[34] !== canAcceptInput) {
    t9 = e_1 => {
      if (!canAcceptInput) {
        return;
      }
      const items = Array.from(e_1.clipboardData.items);
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e_1.preventDefault();
          const file_1 = item.getAsFile();
          if (file_1) {
            addImageFile(file_1);
          }
          break;
        }
      }
    };
    $[33] = addImageFile;
    $[34] = canAcceptInput;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  const handlePaste = t9;
  let t10;
  if ($[36] !== canAcceptInput) {
    t10 = e_2 => {
      e_2.preventDefault();
      e_2.stopPropagation();
      if (canAcceptInput) {
        setIsDragging(true);
      }
    };
    $[36] = canAcceptInput;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  const handleDragOver = t10;
  let t11;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e_3 => {
      e_3.preventDefault();
      e_3.stopPropagation();
      if (dropZoneRef.current && !dropZoneRef.current.contains(e_3.relatedTarget as Node)) {
        setIsDragging(false);
      }
    };
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  const handleDragLeave = t11;
  let t12;
  if ($[39] !== addImageFile || $[40] !== canAcceptInput || $[41] !== defaultUploadTarget) {
    t12 = e_4 => {
      e_4.preventDefault();
      e_4.stopPropagation();
      setIsDragging(false);
      if (!canAcceptInput) {
        return;
      }
      const files_0 = Array.from(e_4.dataTransfer.files);
      for (const file_2 of files_0) {
        if (file_2.type.startsWith("image/")) {
          addImageFile(file_2, defaultUploadTarget);
          if (defaultUploadTarget !== "reference") {
            break;
          }
        }
      }
    };
    $[39] = addImageFile;
    $[40] = canAcceptInput;
    $[41] = defaultUploadTarget;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  const handleDrop = t12;
  let t13;
  let t14;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      const handleWindowDragEnd = () => setIsDragging(false);
      window.addEventListener("dragend", handleWindowDragEnd);
      return () => window.removeEventListener("dragend", handleWindowDragEnd);
    };
    t14 = [];
    $[43] = t13;
    $[44] = t14;
  } else {
    t13 = $[43];
    t14 = $[44];
  }
  useEffect(t13, t14);
  let t15;
  if ($[45] !== setFrameInputs) {
    t15 = target_0 => {
      setFrameInputs(prev_6 => ({
        ...prev_6,
        [target_0]: null
      }));
    };
    $[45] = setFrameInputs;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  const removeFrameImage = t15;
  let t16;
  if ($[47] !== setReferenceImages) {
    t16 = index_1 => {
      setReferenceImages(prev_7 => prev_7.filter((__1, i_1) => i_1 !== index_1));
    };
    $[47] = setReferenceImages;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  const removeReferenceImage = t16;
  let t17;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = target_1 => {
      setUploadTarget(target_1);
      fileInputRef.current?.click();
    };
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  const openFilePicker = t17;
  const t18 = `rounded-md border-input border dark:bg-input/30 shadow-xs focus-within:ring-1 focus-within:ring-ring transition-colors ${isDragging ? "border-primary bg-primary/5 ring-1 ring-primary" : ""}`;
  let t19;
  if ($[50] !== canAcceptInput || $[51] !== isDragging || $[52] !== t) {
    t19 = isDragging && canAcceptInput && <div className="flex items-center justify-center py-4 text-sm text-muted-foreground"><ImagePlus className="mr-2 h-4 w-4" />{t("studio.dropImageHere")}</div>;
    $[50] = canAcceptInput;
    $[51] = isDragging;
    $[52] = t;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== frameInputs.end || $[55] !== frameInputs.start || $[56] !== removeFrameImage || $[57] !== t) {
    t20 = (frameInputs.start ?? frameInputs.end) && <div className="flex flex-wrap gap-2 px-3 pt-3">{[{
        key: "start" as const,
        label: t("studio.firstFrame"),
        image: frameInputs.start
      }, {
        key: "end" as const,
        label: t("studio.lastFrame"),
        image: frameInputs.end
      }].map(item_0 => {
        if (!item_0.image) {
          return null;
        }
        return <div key={item_0.key} className="space-y-1"><div className="px-1 text-xs text-muted-foreground">{item_0.label}</div><div className="group relative h-14 w-14 rounded-md border"><img src={item_0.image.dataUrl} alt={item_0.label} className="size-full rounded-md object-cover" /><button type="button" aria-label={t("studio.removeAttachment")} onClick={() => removeFrameImage(item_0.key)} className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground"><X className="h-3 w-3" /></button></div></div>;
      })}</div>;
    $[54] = frameInputs.end;
    $[55] = frameInputs.start;
    $[56] = removeFrameImage;
    $[57] = t;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== referenceImages || $[60] !== removeReferenceImage || $[61] !== t) {
    t21 = referenceImages.length > 0 && <div className="flex flex-wrap gap-2 px-3 pt-3">{referenceImages.map((image, index_2) => <div key={`${image.dataUrl}-${index_2}`} className="space-y-1"><div className="px-1 text-xs text-muted-foreground">{t("studio.referenceImage", {
            count: index_2 + 1
          })}</div><div className="group relative h-14 w-14 rounded-md border"><img src={image.dataUrl} alt={t("studio.referenceImage", {
            count: index_2 + 1
          })} className="size-full rounded-md object-cover" /><button type="button" aria-label={t("studio.removeReferenceImage")} onClick={() => removeReferenceImage(index_2)} className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground"><X className="h-3 w-3" /></button></div></div>)}</div>;
    $[59] = referenceImages;
    $[60] = removeReferenceImage;
    $[61] = t;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== referenceVideos || $[64] !== removeReferenceVideo || $[65] !== t) {
    t22 = referenceVideos.length > 0 && <div className="flex flex-wrap gap-2 px-3 pt-3">{referenceVideos.map((url_1, index_3) => <div key={`${url_1}-${index_3}`} className="group relative flex max-w-[220px] items-center gap-1.5 rounded-md border bg-muted/40 py-1 pl-2 pr-6 text-xs" title={url_1}><Film className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span className="truncate">{t("studio.referenceVideo", {
            count: index_3 + 1
          })}</span><button type="button" aria-label={t("studio.removeReferenceVideo")} onClick={() => removeReferenceVideo(index_3)} className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground"><X className="h-3 w-3" /></button></div>)}</div>;
    $[63] = referenceVideos;
    $[64] = removeReferenceVideo;
    $[65] = t;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== referenceAudios || $[68] !== removeReferenceAudio || $[69] !== t) {
    t23 = referenceAudios.length > 0 && <div className="flex flex-wrap gap-2 px-3 pt-3">{referenceAudios.map((url_2, index_4) => <div key={`${url_2}-${index_4}`} className="group relative flex max-w-[220px] items-center gap-1.5 rounded-md border bg-muted/40 py-1 pl-2 pr-6 text-xs" title={url_2}><Music className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span className="truncate">{t("studio.referenceAudio", {
            count: index_4 + 1
          })}</span><button type="button" aria-label={t("studio.removeReferenceAudio")} onClick={() => removeReferenceAudio(index_4)} className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground"><X className="h-3 w-3" /></button></div>)}</div>;
    $[67] = referenceAudios;
    $[68] = removeReferenceAudio;
    $[69] = t;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  let t24;
  if ($[71] !== setPrompt) {
    t24 = e_5 => setPrompt(e_5.target.value);
    $[71] = setPrompt;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  let t25;
  if ($[73] !== t) {
    t25 = t("studio.describeVideo");
    $[73] = t;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  let t26;
  if ($[75] !== handleKeyDown || $[76] !== handlePaste || $[77] !== isGenerating || $[78] !== prompt || $[79] !== t24 || $[80] !== t25) {
    t26 = <Textarea ref={textareaRef} value={prompt} onChange={t24} onKeyDown={handleKeyDown} onPaste={handlePaste} placeholder={t25} className="min-h-[80px] max-h-[200px] resize-none border-0 bg-transparent shadow-none focus-visible:ring-0 dark:bg-transparent" disabled={isGenerating} />;
    $[75] = handleKeyDown;
    $[76] = handlePaste;
    $[77] = isGenerating;
    $[78] = prompt;
    $[79] = t24;
    $[80] = t25;
    $[81] = t26;
  } else {
    t26 = $[81];
  }
  let t27;
  if ($[82] !== handleDragOver || $[83] !== handleDrop || $[84] !== handlePaste || $[85] !== t18 || $[86] !== t19 || $[87] !== t20 || $[88] !== t21 || $[89] !== t22 || $[90] !== t23 || $[91] !== t26) {
    t27 = <div ref={dropZoneRef} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onPaste={handlePaste} className={t18}>{t19}{t20}{t21}{t22}{t23}{t26}</div>;
    $[82] = handleDragOver;
    $[83] = handleDrop;
    $[84] = handlePaste;
    $[85] = t18;
    $[86] = t19;
    $[87] = t20;
    $[88] = t21;
    $[89] = t22;
    $[90] = t23;
    $[91] = t26;
    $[92] = t27;
  } else {
    t27 = $[92];
  }
  let t28;
  if ($[93] !== handleFileSelect) {
    t28 = <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />;
    $[93] = handleFileSelect;
    $[94] = t28;
  } else {
    t28 = $[94];
  }
  let t29;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => openFilePicker("frame-start");
    $[95] = t29;
  } else {
    t29 = $[95];
  }
  const t30 = isGenerating || !canUseFrameInputs;
  let t31;
  if ($[96] !== canUseFrameInputs || $[97] !== t) {
    t31 = !canUseFrameInputs ? t("studio.frameInputUnsupported") : undefined;
    $[96] = canUseFrameInputs;
    $[97] = t;
    $[98] = t31;
  } else {
    t31 = $[98];
  }
  let t32;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <ImagePlus className="mr-1.5 h-4 w-4" />;
    $[99] = t32;
  } else {
    t32 = $[99];
  }
  let t33;
  if ($[100] !== frameInputs.start || $[101] !== t) {
    t33 = frameInputs.start ? t("studio.replaceFirst") : t("studio.firstFrame");
    $[100] = frameInputs.start;
    $[101] = t;
    $[102] = t33;
  } else {
    t33 = $[102];
  }
  let t34;
  if ($[103] !== t30 || $[104] !== t31 || $[105] !== t33) {
    t34 = <Button variant="outline" size="sm" onClick={t29} disabled={t30} title={t31}>{t32}{t33}</Button>;
    $[103] = t30;
    $[104] = t31;
    $[105] = t33;
    $[106] = t34;
  } else {
    t34 = $[106];
  }
  let t35;
  if ($[107] !== canUseFrameInputs || $[108] !== frameInputs.end || $[109] !== imageInputRequired || $[110] !== isGenerating || $[111] !== t) {
    t35 = !imageInputRequired && <Button variant="outline" size="sm" onClick={() => openFilePicker("frame-end")} disabled={isGenerating || !canUseFrameInputs} title={!canUseFrameInputs ? t("studio.frameInputUnsupported") : undefined}><ImagePlus className="mr-1.5 h-4 w-4" />{frameInputs.end ? t("studio.replaceLast") : t("studio.lastFrame")}</Button>;
    $[107] = canUseFrameInputs;
    $[108] = frameInputs.end;
    $[109] = imageInputRequired;
    $[110] = isGenerating;
    $[111] = t;
    $[112] = t35;
  } else {
    t35 = $[112];
  }
  let t36;
  if ($[113] !== canUseReferenceInputs || $[114] !== imageInputRequired || $[115] !== isGenerating || $[116] !== referenceImages.length || $[117] !== t) {
    t36 = !imageInputRequired && <Button variant="outline" size="sm" onClick={() => openFilePicker("reference")} disabled={isGenerating || !canUseReferenceInputs || referenceImages.length >= 3} title={!canUseReferenceInputs ? t("studio.referenceImagesUnsupported") : undefined}><ImagePlus className="mr-1.5 h-4 w-4" />{referenceImages.length === 0 ? t("studio.reference") : t("studio.referenceImageCount", {
        count: referenceImages.length
      })}</Button>;
    $[113] = canUseReferenceInputs;
    $[114] = imageInputRequired;
    $[115] = isGenerating;
    $[116] = referenceImages.length;
    $[117] = t;
    $[118] = t36;
  } else {
    t36 = $[118];
  }
  let t37;
  if ($[119] !== addReferenceVideo || $[120] !== canUseReferenceVideoInputs || $[121] !== isGenerating || $[122] !== referenceVideoDraft || $[123] !== referenceVideos.length || $[124] !== t) {
    t37 = canUseReferenceVideoInputs && <div className="flex items-center gap-1.5"><Input value={referenceVideoDraft} onChange={e_6 => setReferenceVideoDraft(e_6.target.value)} onKeyDown={e_7 => {
        if (e_7.key === "Enter") {
          e_7.preventDefault();
          addReferenceVideo();
        }
      }} placeholder={t("studio.referenceVideoUrl")} disabled={isGenerating || referenceVideos.length >= 3} className="h-8 w-[220px] text-sm" /><Button variant="outline" size="sm" onClick={addReferenceVideo} disabled={isGenerating || referenceVideos.length >= 3 || !/^https:\/\//i.test(referenceVideoDraft.trim())} title={t("studio.referenceVideoUrl")}><Plus className="mr-1.5 h-4 w-4" />{referenceVideos.length === 0 ? t("studio.video") : t("studio.referenceVideoCount", {
          count: referenceVideos.length
        })}</Button></div>;
    $[119] = addReferenceVideo;
    $[120] = canUseReferenceVideoInputs;
    $[121] = isGenerating;
    $[122] = referenceVideoDraft;
    $[123] = referenceVideos.length;
    $[124] = t;
    $[125] = t37;
  } else {
    t37 = $[125];
  }
  let t38;
  if ($[126] !== addReferenceAudio || $[127] !== canUseReferenceAudioInputs || $[128] !== isGenerating || $[129] !== referenceAudioDraft || $[130] !== referenceAudios.length || $[131] !== t) {
    t38 = canUseReferenceAudioInputs && <div className="flex items-center gap-1.5"><Input value={referenceAudioDraft} onChange={e_8 => setReferenceAudioDraft(e_8.target.value)} onKeyDown={e_9 => {
        if (e_9.key === "Enter") {
          e_9.preventDefault();
          addReferenceAudio();
        }
      }} placeholder={t("studio.referenceAudioUrl")} disabled={isGenerating || referenceAudios.length >= 3} className="h-8 w-[220px] text-sm" /><Button variant="outline" size="sm" onClick={addReferenceAudio} disabled={isGenerating || referenceAudios.length >= 3 || !/^https:\/\//i.test(referenceAudioDraft.trim())} title={t("studio.referenceAudioUrl")}><Plus className="mr-1.5 h-4 w-4" />{referenceAudios.length === 0 ? t("studio.audioRef") : t("studio.referenceAudioCount", {
          count: referenceAudios.length
        })}</Button></div>;
    $[126] = addReferenceAudio;
    $[127] = canUseReferenceAudioInputs;
    $[128] = isGenerating;
    $[129] = referenceAudioDraft;
    $[130] = referenceAudios.length;
    $[131] = t;
    $[132] = t38;
  } else {
    t38 = $[132];
  }
  let t39;
  if ($[133] !== setVideoSize) {
    t39 = val => setVideoSize(val as VideoSize);
    $[133] = setVideoSize;
    $[134] = t39;
  } else {
    t39 = $[134];
  }
  let t40;
  if ($[135] !== t) {
    t40 = t("studio.resolution");
    $[135] = t;
    $[136] = t40;
  } else {
    t40 = $[136];
  }
  let t41;
  if ($[137] !== t40) {
    t41 = <SelectTrigger size="sm" className="min-w-[160px]"><SelectValue placeholder={t40} /></SelectTrigger>;
    $[137] = t40;
    $[138] = t41;
  } else {
    t41 = $[138];
  }
  let t42;
  if ($[139] !== supportedVideoSizes) {
    t42 = getVideoSizes().map(size => <SelectItem key={size} value={size} disabled={!supportedVideoSizes.includes(size)}>{getVideoSizeLabel(size)}</SelectItem>);
    $[139] = supportedVideoSizes;
    $[140] = t42;
  } else {
    t42 = $[140];
  }
  let t43;
  if ($[141] !== t42) {
    t43 = <SelectContent>{t42}</SelectContent>;
    $[141] = t42;
    $[142] = t43;
  } else {
    t43 = $[142];
  }
  let t44;
  if ($[143] !== t39 || $[144] !== t41 || $[145] !== t43 || $[146] !== videoSize) {
    t44 = <Select value={videoSize} onValueChange={t39}>{t41}{t43}</Select>;
    $[143] = t39;
    $[144] = t41;
    $[145] = t43;
    $[146] = videoSize;
    $[147] = t44;
  } else {
    t44 = $[147];
  }
  const t45 = String(videoDuration);
  let t46;
  if ($[148] !== setVideoDuration) {
    t46 = val_0 => setVideoDuration(Number(val_0) as VideoDuration);
    $[148] = setVideoDuration;
    $[149] = t46;
  } else {
    t46 = $[149];
  }
  let t47;
  if ($[150] !== t) {
    t47 = t("studio.duration");
    $[150] = t;
    $[151] = t47;
  } else {
    t47 = $[151];
  }
  let t48;
  if ($[152] !== t47) {
    t48 = <SelectTrigger size="sm" className="min-w-[100px]"><SelectValue placeholder={t47} /></SelectTrigger>;
    $[152] = t47;
    $[153] = t48;
  } else {
    t48 = $[153];
  }
  let t49;
  if ($[154] !== supportedVideoDurations || $[155] !== t) {
    t49 = getVideoDurations().map(duration => <SelectItem key={duration} value={String(duration)} disabled={!supportedVideoDurations.includes(duration)}>{t("studio.durationSeconds", {
        count: duration
      })}</SelectItem>);
    $[154] = supportedVideoDurations;
    $[155] = t;
    $[156] = t49;
  } else {
    t49 = $[156];
  }
  let t50;
  if ($[157] !== t49) {
    t50 = <SelectContent>{t49}</SelectContent>;
    $[157] = t49;
    $[158] = t50;
  } else {
    t50 = $[158];
  }
  let t51;
  if ($[159] !== t45 || $[160] !== t46 || $[161] !== t48 || $[162] !== t50) {
    t51 = <Select value={t45} onValueChange={t46}>{t48}{t50}</Select>;
    $[159] = t45;
    $[160] = t46;
    $[161] = t48;
    $[162] = t50;
    $[163] = t51;
  } else {
    t51 = $[163];
  }
  const t52 = audioToggleDisabled && !isGenerating ? audioToggleDisabledReason : undefined;
  let t53;
  if ($[164] !== audioEnabled || $[165] !== audioToggleDisabled || $[166] !== setAudioEnabled) {
    t53 = <Switch checked={audioEnabled} onCheckedChange={setAudioEnabled} disabled={audioToggleDisabled} />;
    $[164] = audioEnabled;
    $[165] = audioToggleDisabled;
    $[166] = setAudioEnabled;
    $[167] = t53;
  } else {
    t53 = $[167];
  }
  let t54;
  if ($[168] !== t) {
    t54 = t("studio.audio");
    $[168] = t;
    $[169] = t54;
  } else {
    t54 = $[169];
  }
  let t55;
  if ($[170] !== t54) {
    t55 = <span>{t54}</span>;
    $[170] = t54;
    $[171] = t55;
  } else {
    t55 = $[171];
  }
  let t56;
  if ($[172] !== t52 || $[173] !== t53 || $[174] !== t55) {
    t56 = <label className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm" title={t52}>{t53}{t55}</label>;
    $[172] = t52;
    $[173] = t53;
    $[174] = t55;
    $[175] = t56;
  } else {
    t56 = $[175];
  }
  let t57;
  if ($[176] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <div className="flex-1" />;
    $[176] = t57;
  } else {
    t57 = $[176];
  }
  let t58;
  if ($[177] !== missingRequiredImage || $[178] !== t) {
    t58 = missingRequiredImage && <p className="text-sm text-destructive">{t("studio.missingStartFrame")}</p>;
    $[177] = missingRequiredImage;
    $[178] = t;
    $[179] = t58;
  } else {
    t58 = $[179];
  }
  const t59 = isGenerating || !canGenerate;
  let t60;
  if ($[180] !== isGenerating || $[181] !== t) {
    t60 = isGenerating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("common.generating")}</> : <><Sparkles className="mr-2 h-4 w-4" />{t("common.generate")}</>;
    $[180] = isGenerating;
    $[181] = t;
    $[182] = t60;
  } else {
    t60 = $[182];
  }
  let t61;
  if ($[183] !== onGenerate || $[184] !== t59 || $[185] !== t60) {
    t61 = <Button onClick={onGenerate} disabled={t59} className="min-w-[120px]">{t60}</Button>;
    $[183] = onGenerate;
    $[184] = t59;
    $[185] = t60;
    $[186] = t61;
  } else {
    t61 = $[186];
  }
  let t62;
  if ($[187] !== t34 || $[188] !== t35 || $[189] !== t36 || $[190] !== t37 || $[191] !== t38 || $[192] !== t44 || $[193] !== t51 || $[194] !== t56 || $[195] !== t58 || $[196] !== t61) {
    t62 = <div className="flex flex-wrap items-center gap-2">{t34}{t35}{t36}{t37}{t38}{t44}{t51}{t56}{t57}{t58}{t61}</div>;
    $[187] = t34;
    $[188] = t35;
    $[189] = t36;
    $[190] = t37;
    $[191] = t38;
    $[192] = t44;
    $[193] = t51;
    $[194] = t56;
    $[195] = t58;
    $[196] = t61;
    $[197] = t62;
  } else {
    t62 = $[197];
  }
  let t63;
  if ($[198] !== t27 || $[199] !== t28 || $[200] !== t62) {
    t63 = <div className="border-b bg-background p-4"><div className="max-w-4xl mx-auto space-y-3">{t27}{t28}{t62}</div></div>;
    $[198] = t27;
    $[199] = t28;
    $[200] = t62;
    $[201] = t63;
  } else {
    t63 = $[201];
  }
  return t63;
}